// src/components/DmpNumbers.jsx
import React, { useEffect, useRef, useState } from "react";
import { FaCode, FaRobot, FaBriefcase, FaLightbulb } from "react-icons/fa6";
import { LuLayoutDashboard } from "react-icons/lu";
import { RiGraduationCapLine } from "react-icons/ri";

/* -------------------------------------------------
   Animated Number
------------------------------------------------- */
const AnimatedNumber = ({ target, duration = 1500 }) => {
  const [value, setValue] = useState(0);
  const raf = useRef(null);
  const start = useRef(null);

  useEffect(() => {
    const step = (ts) => {
      if (!start.current) start.current = ts;
      const elapsed = ts - start.current;
      const progress = Math.min(elapsed / duration, 1);
      setValue(Math.floor(progress * target));
      if (progress < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [target, duration]);

  return <span className="dmp-num">{value.toLocaleString()}<span className="dmp-plus">+</span></span>;
};

/* -------------------------------------------------
   Main Component
------------------------------------------------- */
const DmpNumbers = () => {
  const data = [
    { n: 270, l: "Projects", c: "pink", area: "p1" },
    { n: 70, l: "Organizations", c: "pink", area: "p2" },
    { n: 150, l: "Products", c: "yellow", area: "p3" },
    { n: 14000, l: "Proposals", c: "yellow", area: "p4" },
    { n: 290, l: "Contributors", c: "pink", area: "p5" },
    { n: 200, l: "Mentors", c: "pink", area: "p6" },
    // { n: 415, l: "Colleges", c: "yellow", area: "p7" },
    // { n: 30, l: "Companies", c: "yellow", area: "p8" },
  ];

  const css = `
    /* === RESET & BASE === */
    .dmp-container {
      width: 100%;
      max-width: 1200px;
      margin: 0 auto;
      padding: 40px 16px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      box-sizing: border-box;
    }

    .dmp-title {
      font-size: clamp(1.6rem, 5vw, 2.25rem);
      font-weight: 750;
      color: #111827;
      margin-bottom: 32px;
      text-align: center;
      line-height: 1.3;
    }

    /* === GRID LAYOUT === */
    .dmp-grid {
      display: grid;
      gap: 16px;
      margin-bottom: 48px;
      /* Default: mobile-first single column */
      grid-template-columns: 1fr;
    }

    /* Tablet: 2 columns */
    @media (min-width: 641px) {
      .dmp-grid {
        grid-template-columns: repeat(2, 1fr);
      }
    }

    /* Desktop area layout */
    @media (min-width: 1025px) {
      .dmp-grid {
        grid-template-columns: 2.2fr 1fr 1.8fr;
        grid-template-areas:
          "p1 p2 p3"
          "p4 p5 p6"
          "p7 p7 p8";
      }
      .p1 { grid-area: p1; }
      .p2 { grid-area: p2; }
      .p3 { grid-area: p3; }
      .p4 { grid-area: p4; }
      .p5 { grid-area: p5; }
      .p6 { grid-area: p6; }
      .p7 { grid-area: p7; }
      .p8 { grid-area: p8; }
    }

    /* === CARD === */
    .dmp-card {
      padding: 20px;
      border-radius: 12px;
      text-align: center;
      border: 1px solid #f3f4f6;
      background: white;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      min-height: 120px;
    }

    .dmp-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 12px rgba(0,0,0,0.05);
    }

    .dmp-num {
      font-size: clamp(1.8rem, 8vw, 3rem);
      font-weight: 700;
      display: block;
      margin-bottom: 6px;
      line-height: 1.1;
      color: #111827;
    }

    .dmp-label {
      font-size: clamp(0.85rem, 4vw, 1rem);
      color: #374151;
      font-weight: 500;
      text-align: center;
    }

    /* Colors */
    .dmp-pink   { background: #fceff9; }
    .dmp-pink .dmp-num   { color: #86198f; }
    .dmp-yellow { background: #fefbf0; }
    .dmp-yellow .dmp-num { color: #BE9C06; }

    /* === TRACKS & PARTICIPANTS SECTIONS === */
    .dmp-bottom {
      display: flex;
      flex-direction: column;
      gap: 80px;
      align-items: center;
      justify-content: center;
      margin-top: 64px;
      margin-bottom: 32px;
      width: 100%;
    }

    .dmp-section {
      background: none;
      border: none;
      box-shadow: none;
      padding: 0;
      max-width: 100%;
      width: 100%;
    }

    .dmp-section h3 {
      font-size: clamp(1.6rem, 5vw, 2.25rem);
      font-weight: 750;
      color: #111827;
      margin-bottom: 32px;
      text-align: center;
      line-height: 1.3;
    }

    .dmp-track-grid, .dmp-participant-grid {
      display: flex;
      justify-content: center;
      align-items: flex-start;
      gap: 64px;
      margin-bottom: 0;
      flex-wrap: wrap;
    }

    .dmp-track-item, .dmp-participant-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      min-width: 248px;
      max-width: 220px;
    }

    .dmp-track-icon, .dmp-participant-icon {
      font-size: 64px;
      color: #BE9C06;
      margin-bottom: 18px;
    }

    .dmp-track-icon-img, .dmp-participant-icon-img {
      width: 120px;
      height: 120px;
      object-fit: contain;
      margin-bottom: 18px;
      display: block;
    }

    .dmp-track-label, .dmp-participant-label {
      font-size: 1.15rem;
      font-weight: 700;
      color: #7E0054;
      margin-top: 0;
      margin-bottom: 0;
      text-align: center;
      letter-spacing: 0.01em;
    }

    @media (max-width: 900px) {
      .dmp-section h3 {
        font-size: clamp(1.6rem, 5vw, 2.25rem);
      font-weight: 750;
      color: #111827;
      margin-bottom: 32px;
      text-align: center;
      line-height: 1.3;
      }
      .dmp-track-icon-img, .dmp-participant-icon-img {
        width: 80px;
        height: 80px;
        margin-bottom: 10px;
      }
      .dmp-track-grid, .dmp-participant-grid {
        gap: 18px;
      }
    }

    @media (max-width: 640px) {
      .dmp-bottom {
        gap: 40px;
        margin-top: 32px;
      }
      .dmp-track-grid, .dmp-participant-grid {
        flex-direction: column;
        gap: 24px;
        align-items: center;
      }
      .dmp-track-item, .dmp-participant-item {
        min-width: 0;
        max-width: 100%;
      }
      .dmp-track-icon-img, .dmp-participant-icon-img {
        width: 56px;
        height: 56px;
        margin-bottom: 8px;
      }
    }

    /* === MOBILE OPTIMIZATIONS === */
    @media (max-width: 640px) {
      .dmp-container {
        padding: 24px 12px;
      }
      .dmp-title {
        font-size: 1.6rem;
        margin-bottom: 24px;
      }
      .dmp-card {
        padding: 16px;
        min-height: 100px;
      }
      .dmp-num {
        font-size: clamp(1.6rem, 9vw, 2.5rem);
      }
      .dmp-label {
        font-size: 0.85rem;
      }
      .dmp-bottom {
        flex-direction: column;
        align-items: center;
        gap: 20px;
      }
      .dmp-section {
        width: 100%;
        max-width: 100%;
        text-align: center;
        padding: 18px;
      }
      .dmp-section h3 {
        font-size: 1.6rem;
        margin-bottom: 24px;
      }
      .dmp-section li {
        justify-content: center;
        text-align: center;
        font-size: 0.9rem;
      }
      .dmp-ico {
        margin-right: 8px;
      }
    }

    /* Ultra small screens (e.g., iPhone SE) */
    @media (max-width: 360px) {
      .dmp-card {
        padding: 12px;
        min-height: 90px;
      }
      .dmp-num {
        font-size: 1.5rem;
      }
      .dmp-label {
        font-size: 0.8rem;
      }
      .dmp-section h3 {
        font-size: 1.6rem;
        margin-bottom: 24px;
      }
      .dmp-section li {
        font-size: 0.85rem;
      }
      .dmp-ico {
        font-size: 1rem;
      }
    }

  
  `;

  return (
    <div className="dmp-container">
      <style>{css}</style>

      <h2 className="dmp-title">DMP Numbers</h2>

      <div className="dmp-grid">
        {data.map((d) => (
          <div key={d.area} className={`dmp-card dmp-${d.c} ${d.area}`}>
            <AnimatedNumber target={d.n} />
            <div className="dmp-label">{d.l}</div>
          </div>
        ))}
      </div>

      <div className="dmp-bottom">
        <div className="dmp-section">
          <h3>Tracks under DMP</h3>
          <div className="dmp-track-grid">
            <div className="dmp-track-item">
              <img src="/images/coding.png" alt="Coding" className="dmp-track-icon-img" />
              <div className="dmp-track-label">Coding</div>
            </div>
            <div className="dmp-track-item">
              <img src="/images/UI.png" alt="Product Usability & UI/UX" className="dmp-track-icon-img" />
              <div className="dmp-track-label">Product Usability &amp; UI/UX</div>
            </div>
            <div className="dmp-track-item">
              <img src="/images/ai.png" alt="Artificial Intelligence (AI)" className="dmp-track-icon-img" />
              <div className="dmp-track-label">Artificial Intelligence (AI)</div>
            </div>
          </div>
        </div>

        <div className="dmp-section">
          <h3>Who can participate in DMP</h3>
          <div className="dmp-participant-grid">
            <div className="dmp-participant-item">
              <img src="/images/students.png" alt="College Students" className="dmp-participant-icon-img" />
              <div className="dmp-participant-label">College Students</div>
            </div>
            <div className="dmp-participant-item">
              <img src="/images/working.png" alt="Working Professionals" className="dmp-participant-icon-img" />
              <div className="dmp-participant-label">Working Professionals</div>
            </div>
            <div className="dmp-participant-item">
              <img src="/images/code.png" alt="Tech Enthusiasts" className="dmp-participant-icon-img" />
              <div className="dmp-participant-label">Tech Enthusiasts</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DmpNumbers;